/**
 * useFinopsPeriod — estado de período FinOps persistido na URL via react-router.
 *
 * Convenção de `end` (EXCLUSIVO):
 *   O backend trata `end` como exclusivo (intervalo [start, end)).
 *   Para MONTHLY, `end` deve ser o 1º dia do mês SEGUINTE ao último mês desejado.
 *   Exemplo: "mês passado" (maio 2026) → start=2026-05-01, end=2026-06-01.
 *   Isso espelha o padrão de `default_end = 1º dia do mês seguinte` em api_routes.py.
 *
 * Query string: ?start=YYYY-MM-DD&end=YYYY-MM-DD&g=MONTHLY|DAILY
 *
 * Presets disponíveis:
 *   'mtd'        — 1º dia do mês corrente até hoje (parcial; end=hoje+1 dia)
 *   'last_month' — mês passado completo
 *   'last_3m'    — últimos 3 meses incl. corrente (start = 2 meses atrás, end = próximo mês)
 *   'last_12m'   — últimos 12 meses incl. corrente
 *   'custom'     — range livre (setRange)
 */
import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'

// ─────────────────────────────────────────────
// Tipos
// ─────────────────────────────────────────────

export type FinopsPreset = 'mtd' | 'last_month' | 'last_3m' | 'last_12m' | 'custom'
export type FinopsGranularity = 'MONTHLY' | 'DAILY'

export interface UseFinopsPeriodReturn {
  /** Data de início inclusive, formato YYYY-MM-DD */
  start: string
  /** Data de fim EXCLUSIVA, formato YYYY-MM-DD (1º dia do mês seguinte para MONTHLY) */
  end: string
  granularity: FinopsGranularity
  preset: FinopsPreset
  /** Aplica um preset calculando start/end automaticamente e sincroniza URL */
  setPreset: (p: FinopsPreset) => void
  /** Entra em modo 'custom' com o range fornecido (end exclusivo) */
  setRange: (start: string, end: string) => void
  /** Altera apenas a granularidade, preservando start/end */
  setGranularity: (g: FinopsGranularity) => void
}

// ─────────────────────────────────────────────
// Helpers de data (dayjs disponível como dep do projeto)
// ─────────────────────────────────────────────

/** Formata um dayjs para YYYY-MM-DD */
function fmt(d: dayjs.Dayjs): string {
  return d.format('YYYY-MM-DD')
}

/**
 * Calcula start/end para um preset.
 * `end` é SEMPRE exclusivo: 1º dia do mês seguinte ao último mês do range.
 */
function calcPreset(p: FinopsPreset): { start: string; end: string } | null {
  if (p === 'custom') return null

  const today = dayjs()
  const curMonthStart = today.startOf('month')

  switch (p) {
    case 'mtd':
      return {
        start: fmt(curMonthStart),
        // end exclusivo: 1º dia do mês seguinte (o mês corrente está parcial)
        end: fmt(curMonthStart.add(1, 'month')),
      }
    case 'last_month': {
      const lastMonthStart = curMonthStart.subtract(1, 'month')
      return {
        start: fmt(lastMonthStart),
        end: fmt(curMonthStart), // 1º do mês corrente = exclusivo do mês passado
      }
    }
    case 'last_3m': {
      // 3 meses: corrente + 2 anteriores
      const start3m = curMonthStart.subtract(2, 'month')
      return {
        start: fmt(start3m),
        end: fmt(curMonthStart.add(1, 'month')),
      }
    }
    case 'last_12m': {
      const start12m = curMonthStart.subtract(11, 'month')
      return {
        start: fmt(start12m),
        end: fmt(curMonthStart.add(1, 'month')),
      }
    }
    default:
      return null
  }
}

/** Infere o preset a partir de start/end/hoje (best-effort; retorna 'custom' se não encaixar) */
function inferPreset(start: string, end: string): FinopsPreset {
  const today = dayjs()
  const curMonthStart = today.startOf('month')

  const presets: FinopsPreset[] = ['mtd', 'last_month', 'last_3m', 'last_12m']
  for (const p of presets) {
    const calc = calcPreset(p)
    if (calc && calc.start === start && calc.end === end) return p
  }
  return 'custom'
}

/** Defaults MTD (sem query string) */
function defaultPeriod(): { start: string; end: string } {
  const today = dayjs()
  const curMonthStart = today.startOf('month')
  return {
    start: fmt(curMonthStart),
    end: fmt(curMonthStart.add(1, 'month')),
  }
}

// ─────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────

export function useFinopsPeriod(): UseFinopsPeriodReturn {
  const [searchParams, setSearchParams] = useSearchParams()

  const rawStart = searchParams.get('start')
  const rawEnd = searchParams.get('end')
  const rawG = searchParams.get('g')

  // Resolve start/end: URL > MTD default
  const def = defaultPeriod()
  const start = rawStart ?? def.start
  const end = rawEnd ?? def.end
  const granularity: FinopsGranularity =
    rawG === 'DAILY' ? 'DAILY' : 'MONTHLY'

  const preset = inferPreset(start, end)

  const setPreset = useCallback(
    (p: FinopsPreset) => {
      if (p === 'custom') {
        // Não recalcula datas; apenas marca 'custom' — usuário usará setRange
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev)
            // Mantém start/end atuais; não altera datas
            return next
          },
          { replace: true },
        )
        return
      }
      const calc = calcPreset(p)
      if (!calc) return
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('start', calc.start)
          next.set('end', calc.end)
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const setRange = useCallback(
    (newStart: string, newEnd: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('start', newStart)
          next.set('end', newEnd)
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const setGranularity = useCallback(
    (g: FinopsGranularity) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (g === 'MONTHLY') {
            next.delete('g') // MONTHLY é o default; limpa para URL mais limpa
          } else {
            next.set('g', g)
          }
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  return { start, end, granularity, preset, setPreset, setRange, setGranularity }
}
